import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${API_BASE}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Lista dei path pubblici che non richiedono autenticazione
const PUBLIC_PATHS = ['/login', '/forgot-password', '/reset-password', '/verify-otp', '/privacy', '/invite', '/delete-account'];

const isPublicPath = () => {
  return PUBLIC_PATHS.some(p => window.location.pathname.startsWith(p));
};

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      // Non fare redirect se siamo già su una pagina pubblica
      if (!isPublicPath() && !window.location.hash.includes('session_id')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => {
    // Include device_token from localStorage if available
    const deviceToken = localStorage.getItem('trusted_device_token');
    if (deviceToken && !data.device_token) {
      data.device_token = deviceToken;
    }
    return api.post('/auth/login', data);
  },
  verifyOtp: (data) => api.post('/auth/verify-otp', data),
  resendOtp: (userId) => api.post(`/auth/resend-otp?user_id=${userId}`),
  googleSession: (sessionId) => api.get('/auth/session', { headers: { 'X-Session-ID': sessionId } }),
  googleLogin: (data) => api.post('/auth/google/mobile', data),
  appleLogin: (data) => api.post('/auth/apple-login', data),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
  verifyResetToken: (token) => api.get(`/auth/verify-reset-token?token=${token}`),
  // 2FA Settings
  get2FAStatus: () => api.get('/auth/2fa/status'),
  toggle2FA: (enabled) => api.put('/auth/2fa/toggle', { enabled }),
  // Trusted Devices
  getTrustedDevices: () => api.get('/auth/trusted-devices'),
  removeTrustedDevice: (deviceId) => api.delete(`/auth/trusted-devices/${deviceId}`),
  removeAllTrustedDevices: () => api.delete('/auth/trusted-devices'),
  // Account Management
  changePassword: (currentPassword, newPassword) => api.post('/auth/change-password', { 
    current_password: currentPassword, 
    new_password: newPassword 
  }),
  deleteAccount: (password, confirmation = 'DELETE') => api.delete('/auth/delete-account', { 
    data: { password, confirmation } 
  }),
};

// WebAuthn
export const webauthnAPI = {
  getRegistrationOptions: (deviceName) => api.post('/webauthn/register/options', { device_name: deviceName }),
  verifyRegistration: (credential, deviceName) => api.post('/webauthn/register/verify', { credential, device_name: deviceName }),
  getAuthenticationOptions: (email) => api.post('/webauthn/authenticate/options', { email }),
  verifyAuthentication: (email, credential) => api.post('/webauthn/authenticate/verify', { email, credential }),
  getCredentials: () => api.get('/webauthn/credentials'),
  deleteCredential: (credentialId) => api.delete(`/webauthn/credentials/${credentialId}`),
  checkAvailable: (email) => api.get(`/webauthn/check/${email}`),
};

// Users
export const usersAPI = {
  getAll: () => api.get('/users'),
  update: (userId, data) => api.put(`/users/${userId}`, data),
  updateSubscription: (userId, data) => api.put(`/users/${userId}/subscription`, data),
  delete: (userId) => api.delete(`/users/${userId}`),
};

// Links
export const linksAPI = {
  getAll: (userId) => api.get('/links', { params: userId ? { user_id: userId } : {} }),
  create: (data) => api.post('/links', data),
  update: (linkId, data) => api.put(`/links/${linkId}`, data),
  delete: (linkId) => api.delete(`/links/${linkId}`),
};

// Subscription
export const subscriptionAPI = {
  get: () => api.get('/subscription'),
  upgrade: (plan, billingPeriod) => api.post('/subscription/upgrade', { plan, billing_period: billingPeriod }),
  getPublicConfig: () => api.get('/subscription-config/public'),
  getMobileLinks: () => api.get('/mobile-links/public'),
};

// Admin Config (root only)
export const adminAPI = {
  getSubscriptionConfig: () => api.get('/admin/subscription-config'),
  updateSubscriptionConfig: (data) => api.put('/admin/subscription-config', data),
  getLinkTokens: () => api.get('/admin/link-tokens'),
  createLinkToken: (data) => api.post('/admin/link-tokens', data),
  updateLinkToken: (tokenId, data) => api.put(`/admin/link-tokens/${tokenId}`, data),
  deleteLinkToken: (tokenId) => api.delete(`/admin/link-tokens/${tokenId}`),
  getTokenInstructions: () => api.get('/settings/token-instructions'),
  updateTokenInstructions: (instructions) => api.put('/admin/token-instructions', { instructions }),
  // SMTP Config
  getSmtpConfig: () => api.get('/admin/smtp-config'),
  updateSmtpConfig: (data) => api.put('/admin/smtp-config', data),
  testSmtpConfig: () => api.post('/admin/smtp-config/test'),
  // Stripe Config
  getStripeConfig: () => api.get('/admin/stripe-config'),
  updateStripeConfig: (data) => api.put('/admin/stripe-config', data),
  // Accounting Email Config
  getAccountingEmailConfig: () => api.get('/admin/accounting-email-config'),
  updateAccountingEmailConfig: (data) => api.put('/admin/accounting-email-config', data),
  // Ticket Email Config
  getTicketEmailConfig: () => api.get('/admin/ticket-email-config'),
  updateTicketEmailConfig: (data) => api.put('/admin/ticket-email-config', data),
  // Mobile App Links Config
  getMobileLinksConfig: () => api.get('/admin/mobile-links-config'),
  updateMobileLinksConfig: (data) => api.put('/admin/mobile-links-config', data),
  // API Tokens
  getApiTokens: () => api.get('/admin/api-tokens'),
  createApiToken: (data) => api.post('/admin/api-tokens', data),
  deleteApiToken: (tokenId) => api.delete(`/admin/api-tokens/${tokenId}`),
  toggleApiToken: (tokenId) => api.put(`/admin/api-tokens/${tokenId}/toggle`),
  // User OBC Details
  getUserObcs: (userId) => api.get(`/admin/user-obcs/${userId}`),
  getObcByName: (obcName) => api.get(`/admin/obc-by-name/${encodeURIComponent(obcName)}`),
  getTokenDetails: (tokenCode) => api.get(`/admin/token-details/${encodeURIComponent(tokenCode)}`),
};

// Payments
export const paymentsAPI = {
  createSubscriptionCheckout: (planId, originUrl) => api.post('/payments/create-subscription-checkout', { plan_id: planId, origin_url: originUrl }),
  createTicketsCheckout: (quantity, originUrl) => api.post('/payments/create-tickets-checkout', { quantity, origin_url: originUrl }),
  getSessionStatus: (sessionId) => api.get(`/payments/session-status/${sessionId}`),
};

// Token redemption
export const tokenAPI = {
  getInstructions: () => api.get('/settings/token-instructions'),
  redeem: (tokenCode) => api.post('/links/redeem', { token_code: tokenCode }),
  redeemForUser: (tokenCode, userId) => api.post('/links/redeem-for-user', { token_code: tokenCode, user_id: userId }),
};

// Tickets
export const ticketsAPI = {
  getAll: () => api.get('/tickets'),
  get: (ticketId) => api.get(`/tickets/${ticketId}`),
  create: (data) => api.post('/tickets', data),
  updateStatus: (ticketId, status) => api.put(`/tickets/${ticketId}/status`, { status }),
  addMessage: (ticketId, text) => api.post(`/tickets/${ticketId}/messages`, { text }),
  delete: (ticketId) => api.delete(`/tickets/${ticketId}`),
  getUsage: () => api.get('/tickets/stats'),
  purchaseExtra: (quantity) => api.post('/tickets/purchase-extra', { quantity }),
  getExtraBalance: () => api.get('/tickets/extra-balance'),
  getUsersStats: () => api.get('/tickets/user-stats'),
};

// Billing
export const billingAPI = {
  get: () => api.get('/billing'),
  update: (data) => api.put('/billing', data),
  getForUser: (userId) => api.get(`/admin/billing/${userId}`),
};

// Notifications
export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  markRead: (notifId) => api.put(`/notifications/${notifId}/read`),
  markAllRead: () => api.put('/notifications/mark-all-read'),
};

// Admin API Keys
export const apiKeysAPI = {
  getAll: () => api.get('/admin/api-keys'),
  create: () => api.post('/admin/api-keys'),
  revoke: (keyId) => api.delete(`/admin/api-keys/${keyId}`),
};

// Stats
export const statsAPI = {
  get: () => api.get('/stats'),
};

// Audit Logs
export const auditAPI = {
  getLogs: (params) => api.get('/audit/logs', { params }),
  getStats: () => api.get('/audit/stats'),
  getActions: () => api.get('/audit/actions'),
  cleanup: () => api.delete('/audit/cleanup'),
};

export default api;
